// When this person last read the activity feed. Kept per-browser rather than in
// Firestore so one person marking things read never clears the other's badge.
const KEY = 'flightdeck-last-seen';

export function readSeen(): number {
  const v = Number(localStorage.getItem(KEY));
  return Number.isFinite(v) && v > 0 ? v : 0;
}

export function markSeen(at: number): void {
  localStorage.setItem(KEY, String(at));
}

/*
 * WHEN THIS PERSON LAST OPENED EACH CARD.
 *
 * Alongside the feed marker above and for the same reason — kept per browser, so
 * one person reading something never clears somebody else's mark. A map rather
 * than one timestamp, because the question is per card: "has anything happened
 * on THIS since I read it" cannot be answered by when I last read something else.
 *
 * Bounded, because it grows by one entry per card ever opened and localStorage is
 * a few megabytes shared with everything else. The oldest are dropped first: a
 * card nobody has opened in five hundred cards' time is one whose mark nobody is
 * waiting on.
 */
const CARDS_KEY = 'flightdeck-cards-seen';
const KEEP = 500;

export function readCardsSeen(): Record<string, number> {
  try {
    const raw = JSON.parse(localStorage.getItem(CARDS_KEY) || '{}') as unknown;
    return raw && typeof raw === 'object' ? (raw as Record<string, number>) : {};
  } catch {
    // Corrupt or unreadable is the same as never having looked, which marks
    // everything once rather than losing anything.
    return {};
  }
}

export function markCardSeen(id: string, at: number): Record<string, number> {
  const all = { ...readCardsSeen(), [id]: at };
  const trimmed = Object.entries(all)
    .sort((a, b) => b[1] - a[1])
    .slice(0, KEEP);
  const next = Object.fromEntries(trimmed);
  try {
    localStorage.setItem(CARDS_KEY, JSON.stringify(next));
  } catch {
    // A full or refused store is not worth failing an issue being opened over.
  }
  return next;
}

/*
 * WHICH DECIDED REQUESTS THIS PERSON HAS CLEARED FROM THEIR OWN LIST.
 *
 * The requester side of a board request — #359. Once an admin has approved or
 * declined one, there is nothing left for the asker to do with it, so they get
 * an X to clear it away. That is a view convenience and nothing more: it must
 * NOT delete the row, because the admin keeps a Decided log that the same row
 * feeds. So the clearing is kept here, per browser, exactly like the marks
 * above — one person tidying their view never touches anyone else's, and never
 * the record. Bounded the same way; a request cleared five hundred requests ago
 * is one whose mark nobody is waiting on.
 */
const CLEARED_KEY = 'flightdeck-requests-cleared';

export function readRequestsCleared(): Record<string, number> {
  try {
    const raw = JSON.parse(localStorage.getItem(CLEARED_KEY) || '{}') as unknown;
    return raw && typeof raw === 'object' ? (raw as Record<string, number>) : {};
  } catch {
    return {};
  }
}

export function markRequestCleared(id: string, at: number): Record<string, number> {
  const all = { ...readRequestsCleared(), [id]: at };
  const trimmed = Object.entries(all)
    .sort((a, b) => b[1] - a[1])
    .slice(0, KEEP);
  const next = Object.fromEntries(trimmed);
  try {
    localStorage.setItem(CLEARED_KEY, JSON.stringify(next));
  } catch {
    // A full or refused store is no reason to fail clearing a row from a view.
  }
  return next;
}
